import type { ChatSessionFilterInput, CreateChatSessionInput, DefaultPromptInput } from "@qw/graphql";
import { useDefaultPromptQuery } from "@qw/graphql";
import type { MCPPlatform } from "@qw/qw-common";
import { cn, Spinner } from "@qw/ui";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import { adaptChatMessagesToUIMessages } from "../chat/chat.helpers";
import type { FileResourceLinkHandler, ResourceLinkHandler, SuggestionInsertSlot } from "../chat/chat.types";
import { Chat } from "../chat/components/chat";
import type { ChatFooterVariant } from "../chat/components/chat-footer";
import type { ChatHeaderProps } from "../chat/components/chat-header";
import type { MissingChatSessionProps } from "../chat/components/missing-chat-session";
import { usePlaybookRunTree } from "../chat/hooks/use-playbook-run-tree";
import { useTrackActiveChatSession } from "../chat/hooks/use-unread-sessions";
import { ChatProvider, type ToolFilter } from "../chat/providers/chat-provider";
import { ChatSessionProvider, useChatSession } from "../chat/providers/chat-session-provider";
import { getDismissedCompactRunId, setDismissedCompactRunId } from "./compact-playbook-dismissal";
import {
  type CompactPlaybookDisplayMode,
  deriveCompactPlaybookDisplayMode,
  resolveActivePlaybookMode,
} from "./compact-playbook-mode";
import { CompactPlaybookRunning, RestoringPlaybookTranscript } from "./compact-playbook-running";
import { useCompactEligibleRootRunId } from "./use-compact-eligible-root-run-id";

export interface ChatPanelProps {
  apiUrl: string;
  allowCreateChatSession?: boolean;
  chatSessionId?: string | null;
  chatSessionsFilter: Omit<ChatSessionFilterInput & CreateChatSessionInput, "userId">;
  className?: string;
  defaultPromptInput: DefaultPromptInput;
  fileResourceLinkHandler?: FileResourceLinkHandler;
  footerVariant?: ChatFooterVariant;
  header?: ChatHeaderProps;
  platform: MCPPlatform;
  promptInput?: DefaultPromptInput;
  resourceLinkHandlers?: Record<string, ResourceLinkHandler>;
  suggestionInsert?: SuggestionInsertSlot;
  toolFilter?: ToolFilter | null;
  toolFilterStorageKey: string;
  emptyComponent?: ReactNode;
  missingSessionProps?: MissingChatSessionProps;
  onClose?: () => void;
  /**
   * Controlled-selection handler for the filtered (multi-session) mode.
   * When set, selection is controlled — the consumer owns it (e.g. inbox
   * BookChatPanel keeps it in the URL) and feeds it back via
   * {@link selectedSessionId}. See `ChatSessionProvider`.
   */
  onSelectSession?: (next: { id: string } | null) => void;
  /** Controlled-selection value (see {@link onSelectSession}). */
  selectedSessionId?: string | null;
  supportsMultipleSessions?: boolean;
  compactActivePlaybooks?: boolean;
}

type ChatPanelContentProps = Pick<
  ChatPanelProps,
  | "apiUrl"
  | "className"
  | "compactActivePlaybooks"
  | "defaultPromptInput"
  | "emptyComponent"
  | "fileResourceLinkHandler"
  | "footerVariant"
  | "header"
  | "platform"
  | "promptInput"
  | "resourceLinkHandlers"
  | "suggestionInsert"
  | "toolFilter"
  | "toolFilterStorageKey"
>;

const ChatPanelContent = ({
  apiUrl,
  className,
  compactActivePlaybooks = false,
  defaultPromptInput,
  emptyComponent,
  fileResourceLinkHandler,
  footerVariant,
  header,
  platform,
  promptInput,
  resourceLinkHandlers,
  suggestionInsert,
  toolFilter,
  toolFilterStorageKey,
}: ChatPanelContentProps) => {
  const { chatSession } = useChatSession();

  useTrackActiveChatSession(chatSession?.id);

  const { data: defaultPromptData } = useDefaultPromptQuery({
    variables: {
      input: defaultPromptInput,
    },
  });
  const systemPrompt = defaultPromptData?.defaultPrompt?.systemPrompt;

  const { playbookRuns, isLoading, refetchPlaybookRuns } = usePlaybookRunTree(chatSession?.id, compactActivePlaybooks);

  const activePlaybookMode = useMemo(() => resolveActivePlaybookMode(playbookRuns), [playbookRuns]);
  const activeRootRun = compactActivePlaybooks ? (activePlaybookMode?.rootRun ?? null) : null;
  const activeRootRunId = useCompactEligibleRootRunId(activeRootRun, chatSession?.status);
  // Re-read per run rather than once per mount: the run tree resolves after the first render, so a
  // mount-only read would decide the dismissal before there is a run to compare it against.
  const dismissedRunId = useMemo(
    () => (chatSession ? getDismissedCompactRunId(chatSession.id) : null),
    [chatSession, activeRootRunId]
  );
  const [runDisplayMode, setRunDisplayMode] = useState<CompactPlaybookDisplayMode>(() =>
    deriveCompactPlaybookDisplayMode({ kind: "normal" }, compactActivePlaybooks, activeRootRunId, dismissedRunId)
  );

  if (!isLoading) {
    const nextMode = deriveCompactPlaybookDisplayMode(
      runDisplayMode,
      compactActivePlaybooks,
      activeRootRunId,
      dismissedRunId
    );
    if (nextMode !== runDisplayMode) {
      setRunDisplayMode(nextMode);
    }
  }

  const showLiveTranscript = useCallback(() => {
    if (!activeRootRunId || !chatSession) {
      return;
    }
    setDismissedCompactRunId(chatSession.id, activeRootRunId);
    setRunDisplayMode({ kind: "live", runId: activeRootRunId });
  }, [activeRootRunId, chatSession]);

  const handleHistoryRefreshComplete = useCallback(() => {
    setRunDisplayMode((current) => (current.kind === "restoring" ? { kind: "normal" } : current));
  }, []);

  const isCompact = runDisplayMode.kind === "compact";
  const isRestoringTranscript = runDisplayMode.kind === "restoring";
  const shouldPrepareTranscript = !isLoading && !isCompact && !isRestoringTranscript;
  const initialMessages = useMemo(
    () => (shouldPrepareTranscript ? adaptChatMessagesToUIMessages(chatSession?.messages ?? []) : []),
    [chatSession?.messages, shouldPrepareTranscript]
  );

  if (!systemPrompt || !chatSession || isLoading) {
    return (
      <div className="px-4 py-5">
        <Spinner />
      </div>
    );
  }

  return (
    <div className={cn("bg-inherit flex h-full min-h-0 min-w-0 overflow-x-hidden flex-col", className)}>
      <ChatProvider
        apiUrl={apiUrl}
        chatSession={chatSession}
        fileResourceLinkHandler={fileResourceLinkHandler}
        initialMessages={initialMessages}
        systemPrompt={systemPrompt}
        platform={platform}
        promptInput={promptInput ?? defaultPromptInput}
        resourceLinkHandlers={resourceLinkHandlers}
        toolFilter={toolFilter}
        toolFilterStorageKey={toolFilterStorageKey}
        liveStreamEnabled={!isCompact && !isRestoringTranscript}
      >
        {isCompact && activePlaybookMode ? (
          <CompactPlaybookRunning
            activeRun={activePlaybookMode.activeRun}
            header={header}
            onPlaybookRunChanged={refetchPlaybookRuns}
            onShowLiveTranscript={showLiveTranscript}
            rootRun={activePlaybookMode.rootRun}
          />
        ) : isRestoringTranscript ? (
          <RestoringPlaybookTranscript header={header} onComplete={handleHistoryRefreshComplete} />
        ) : (
          <Chat
            emptyComponent={emptyComponent}
            footerVariant={footerVariant}
            header={header}
            suggestionInsert={suggestionInsert}
          />
        )}
      </ChatProvider>
    </div>
  );
};

export const ChatPanel = ({
  apiUrl,
  allowCreateChatSession,
  chatSessionId,
  chatSessionsFilter,
  className,
  compactActivePlaybooks,
  defaultPromptInput,
  emptyComponent,
  fileResourceLinkHandler,
  footerVariant,
  header,
  missingSessionProps,
  onClose,
  onSelectSession,
  platform,
  selectedSessionId,
  promptInput,
  resourceLinkHandlers,
  suggestionInsert,
  supportsMultipleSessions,
  toolFilter,
  toolFilterStorageKey,
}: ChatPanelProps) => {
  return (
    <ChatSessionProvider
      allowCreateChatSession={allowCreateChatSession}
      chatSessionId={chatSessionId}
      chatSessionsFilter={chatSessionsFilter}
      missingSessionProps={missingSessionProps}
      onClose={onClose}
      onSelectSession={onSelectSession}
      selectedSessionId={selectedSessionId}
      supportsMultipleSessions={supportsMultipleSessions}
    >
      <ChatPanelContent
        apiUrl={apiUrl}
        className={className}
        compactActivePlaybooks={compactActivePlaybooks}
        defaultPromptInput={defaultPromptInput}
        emptyComponent={emptyComponent}
        fileResourceLinkHandler={fileResourceLinkHandler}
        footerVariant={footerVariant}
        header={header}
        platform={platform}
        promptInput={promptInput}
        resourceLinkHandlers={resourceLinkHandlers}
        suggestionInsert={suggestionInsert}
        toolFilter={toolFilter}
        toolFilterStorageKey={toolFilterStorageKey}
      />
    </ChatSessionProvider>
  );
};
